import { useState, useEffect } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { ASSETS } from "@/constants/assets";
import { Navigation } from "@/components/sections/Navigation";
import { HeroSection } from "@/components/sections/HeroSection";
import { ImageShowcase } from "@/components/sections/ImageShowcase";
import { ServicesSection } from "@/components/sections/ServicesSection";
import { FeatureSection } from "@/components/sections/FeatureSection";
import { DomainsSection } from "@/components/sections/DomainsSection";
import { FrameworkSection } from "@/components/sections/FrameworkSection";
import { ArchetypesSection } from "@/components/sections/ArchetypesSection";
import { TechnologySection } from "@/components/sections/TechnologySection";
import { CTASection } from "@/components/sections/CTASection";
import { Footer } from "@/components/sections/Footer";

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll for anchor links
  useEffect(() => {
    const handleClick = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href === '#') return;
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Remove platform badge
  useEffect(() => {
    const remove = () => { const b = document.getElementById('emergent-badge'); if (b) b.remove(); };
    remove();
    const iv = setInterval(remove, 300);
    const obs = new MutationObserver(remove);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => { clearInterval(iv); obs.disconnect(); };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A1628]" data-testid="app-container">
      <Toaster position="top-center" richColors />
      <div className="grid-bg" />

      <Navigation
        scrolled={scrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main>
        <HeroSection />
        <ImageShowcase />
        <ServicesSection />

        <div className="separator" />

        <FeatureSection
          image={ASSETS.doctorData}
          label="Medical Practices"
          title="Streamline Clinical Operations"
          description={[
            "Reduce documentation burden with AI-assisted intake and summaries. Give clinicians multi-domain insight with standardized programs and protocols."
          ]}
          features={[
            "Digital intake and documentation",
            "AI-assisted clinical summaries",
            "Multi-domain patient views",
            "Automated patient engagement"
          ]}
          logoOverlay
        />

        <DomainsSection />

        <FeatureSection
          reverse
          image={ASSETS.medicalTeam}
          label="Wellness Centers"
          title="Unify Care Across Service Lines"
          description={[
            "Standardize protocols for metabolic health, hormones, IV therapy, and recovery. Scale your care model across providers and locations."
          ]}
          features={[
            "Shared protocol library",
            "Program enrollment tracking",
            "Outcome trend analysis",
            "Multi-location standardization"
          ]}
          logoOverlay
        />

        <div className="separator" />

        <FrameworkSection />

        <FeatureSection
          image={ASSETS.aiDashboard}
          label="MSOs & Health Clubs"
          title="MSO-Grade Analytics & Scale"
          description={[
            "Compare locations, standardize best practices, and drive engagement across corporate wellness programs with clinical offerings."
          ]}
          features={[
            "Multi-site dashboards",
            "Clinician workload analytics",
            "Corporate wellness integration",
            "Role-based permissions"
          ]}
          logoOverlay
        />

        <div className="separator" />

        <ArchetypesSection />
        <TechnologySection />
        <CTASection />
      </main>

      <Footer />
    </div>
  );
}

export default App;
